import type { Constraints, GenerationResult, Metrics, Ticket, ZonePreset } from './types'

export const RED_MAX = 33
export const BLUE_MAX = 16
export const PRICE_PER_TICKET = 2
export const MAX_EXPANDED_TICKETS = 1000

const redUniverse = Array.from({ length: RED_MAX }, (_, i) => i + 1)
const blueUniverse = Array.from({ length: BLUE_MAX }, (_, i) => i + 1)

function randomIndex(maxExclusive: number): number {
  if (maxExclusive <= 1) return 0
  const range = 0x100000000
  const limit = range - (range % maxExclusive)
  const buffer = new Uint32Array(1)
  let value = range
  while (value >= limit) {
    crypto.getRandomValues(buffer)
    value = buffer[0]
  }
  return value % maxExclusive
}

export function secureShuffle<T>(input: readonly T[]): T[] {
  const arr = [...input]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1)
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

export function formatNumber(value: number): string {
  return String(value).padStart(2, '0')
}

export function ticketKey(ticket: Ticket): string {
  return `${ticket.reds.join('-')}|${ticket.blue}`
}

export function calculateMetrics(reds: readonly number[]): Metrics {
  const sorted = [...reds].sort((a, b) => a - b)
  const sum = sorted.reduce((acc, n) => acc + n, 0)
  const odd = sorted.filter(n => n % 2 === 1).length
  const big = sorted.filter(n => n >= 17).length
  const zones: [number, number, number] = [
    sorted.filter(n => n <= 11).length,
    sorted.filter(n => n >= 12 && n <= 22).length,
    sorted.filter(n => n >= 23).length,
  ]

  let consecutivePairs = 0
  const tails = new Map<number, number>()
  const diffs = new Set<number>()

  for (let i = 0; i < sorted.length; i++) {
    tails.set(sorted[i] % 10, (tails.get(sorted[i] % 10) ?? 0) + 1)
    for (let j = i + 1; j < sorted.length; j++) {
      const diff = sorted[j] - sorted[i]
      diffs.add(diff)
      if (diff === 1) consecutivePairs++
    }
  }

  let sameTailPairs = 0
  for (const count of tails.values()) {
    if (count > 1) sameTailPairs += (count * (count - 1)) / 2
  }

  return {
    sum,
    span: sorted[sorted.length - 1] - sorted[0],
    odd,
    even: sorted.length - odd,
    big,
    small: sorted.length - big,
    zones,
    consecutivePairs,
    sameTailPairs,
    ac: Math.max(0, diffs.size - (sorted.length - 1)),
  }
}

function zoneMatches(zones: Metrics['zones'], preset: ZonePreset): boolean {
  if (preset === 'any') return true
  return zones.join('-') === preset
}

export function validateReds(reds: readonly number[], constraints: Constraints): boolean {
  if (reds.length !== 6 || new Set(reds).size !== 6) return false
  const metrics = calculateMetrics(reds)
  if (constraints.oddCount !== undefined && metrics.odd !== constraints.oddCount) return false
  if (constraints.bigCount !== undefined && metrics.big !== constraints.bigCount) return false
  if (constraints.sumMin !== undefined && metrics.sum < constraints.sumMin) return false
  if (constraints.sumMax !== undefined && metrics.sum > constraints.sumMax) return false
  if (constraints.maxConsecutivePairs !== undefined && metrics.consecutivePairs > constraints.maxConsecutivePairs) return false
  return zoneMatches(metrics.zones, constraints.zonePreset)
}

export function validateConstraints(constraints: Constraints): string | null {
  const required = [...new Set(constraints.requiredRed)]
  const excluded = new Set(constraints.excludedRed)

  if (constraints.count < 1 || constraints.count > 200) return '单次生成数量需在 1–200 注之间。'
  if (required.length > 6) return '必选红球不能超过 6 个。'
  if (required.some(n => excluded.has(n))) return '同一个红球不能同时设为必选和排除。'

  const available = redUniverse.filter(n => !excluded.has(n))
  if (available.length < 6) return '可用红球不足 6 个，请减少排除号码。'

  if (constraints.oddCount !== undefined) {
    const requiredOdd = required.filter(n => n % 2 === 1).length
    if (requiredOdd > constraints.oddCount) return '必选红球中的奇数数量已超过奇偶条件。'
  }

  if (constraints.bigCount !== undefined) {
    const requiredBig = required.filter(n => n >= 17).length
    if (requiredBig > constraints.bigCount) return '必选红球中的大号数量已超过大小条件。'
  }

  if (
    constraints.sumMin !== undefined &&
    constraints.sumMax !== undefined &&
    constraints.sumMin > constraints.sumMax
  ) {
    return '和值最小值不能大于最大值。'
  }

  if (constraints.excludedBlue.length >= BLUE_MAX) return '蓝球不能全部排除。'
  return null
}

export function generateConditional(
  constraints: Constraints,
  blockedKeys: ReadonlySet<string> = new Set(),
): GenerationResult {
  const error = validateConstraints(constraints)
  if (error) return { tickets: [], attempts: 0, warning: error }

  const required = [...new Set(constraints.requiredRed)].sort((a, b) => a - b)
  const excluded = new Set(constraints.excludedRed)
  const candidates = redUniverse.filter(n => !excluded.has(n) && !required.includes(n))
  const blueCandidates = blueUniverse.filter(n => !constraints.excludedBlue.includes(n))
  const tickets: Ticket[] = []
  const seen = new Set<string>()
  const maxAttempts = Math.max(5000, constraints.count * 3000)
  let attempts = 0

  while (tickets.length < constraints.count && attempts < maxAttempts) {
    attempts++
    const need = 6 - required.length
    const reds = [...required, ...secureShuffle(candidates).slice(0, need)].sort((a, b) => a - b)
    if (!validateReds(reds, constraints)) continue

    const blue = blueCandidates[randomIndex(blueCandidates.length)]
    const ticket = { reds, blue }
    const key = ticketKey(ticket)
    if (seen.has(key) || blockedKeys.has(key)) continue

    seen.add(key)
    tickets.push(ticket)
  }

  return {
    tickets,
    attempts,
    warning:
      tickets.length < constraints.count
        ? `当前条件较严格，仅找到 ${tickets.length} 注不重复组合。可以适当放宽条件。`
        : undefined,
  }
}

export function quickGenerate(count: number, blockedKeys: ReadonlySet<string> = new Set()): GenerationResult {
  return generateConditional(
    {
      count,
      requiredRed: [],
      excludedRed: [],
      excludedBlue: [],
      zonePreset: 'any',
    },
    blockedKeys,
  )
}

export function combinations<T>(values: readonly T[], choose: number, limit = Number.POSITIVE_INFINITY): T[][] {
  if (choose < 0 || choose > values.length || limit <= 0) return []
  if (choose === 0) return [[]]

  const result: T[][] = []
  const walk = (start: number, picked: T[]) => {
    if (result.length >= limit) return
    if (picked.length === choose) {
      result.push([...picked])
      return
    }
    const remaining = choose - picked.length
    for (let i = start; i <= values.length - remaining; i++) {
      picked.push(values[i])
      walk(i + 1, picked)
      picked.pop()
      if (result.length >= limit) return
    }
  }
  walk(0, [])
  return result
}

export function combinationCount(n: number, k: number): number {
  if (k < 0 || k > n) return 0
  k = Math.min(k, n - k)
  let result = 1
  for (let i = 1; i <= k; i++) result = (result * (n - k + i)) / i
  return Math.round(result)
}

export function expandDantuo(dan: number[], tuo: number[], blues: number[]): GenerationResult {
  const uniqueDan = [...new Set(dan)].sort((a, b) => a - b)
  const uniqueTuo = [...new Set(tuo.filter(n => !uniqueDan.includes(n)))].sort((a, b) => a - b)
  const uniqueBlues = [...new Set(blues)].sort((a, b) => a - b)

  if (uniqueDan.length < 1 || uniqueDan.length >= 6) {
    return { tickets: [], attempts: 0, warning: '胆拖模式需要 1–5 个胆码。' }
  }

  const need = 6 - uniqueDan.length
  if (uniqueTuo.length < need) return { tickets: [], attempts: 0, warning: `至少还需要 ${need} 个拖码。` }
  if (uniqueBlues.length < 1) return { tickets: [], attempts: 0, warning: '请至少选择 1 个蓝球。' }

  const total = combinationCount(uniqueTuo.length, need) * uniqueBlues.length
  const comboLimit = Math.max(1, Math.ceil(MAX_EXPANDED_TICKETS / uniqueBlues.length))
  const redCombos = combinations(uniqueTuo, need, comboLimit)
  const tickets: Ticket[] = []

  outer: for (const combo of redCombos) {
    const reds = [...uniqueDan, ...combo].sort((a, b) => a - b)
    for (const blue of uniqueBlues) {
      tickets.push({ reds, blue })
      if (tickets.length >= MAX_EXPANDED_TICKETS) break outer
    }
  }

  return {
    tickets,
    attempts: redCombos.length,
    warning: total > MAX_EXPANDED_TICKETS ? `共有 ${total} 注，页面仅展开前 ${MAX_EXPANDED_TICKETS} 注。` : undefined,
  }
}

export function expandMultiple(reds: number[], blues: number[]): GenerationResult {
  const uniqueReds = [...new Set(reds)].sort((a, b) => a - b)
  const uniqueBlues = [...new Set(blues)].sort((a, b) => a - b)

  if (uniqueReds.length < 6) return { tickets: [], attempts: 0, warning: '复式至少需要选择 6 个红球。' }
  if (uniqueBlues.length < 1) return { tickets: [], attempts: 0, warning: '复式至少需要选择 1 个蓝球。' }

  const total = combinationCount(uniqueReds.length, 6) * uniqueBlues.length
  const comboLimit = Math.max(1, Math.ceil(MAX_EXPANDED_TICKETS / uniqueBlues.length))
  const redCombos = combinations(uniqueReds, 6, comboLimit)
  const tickets: Ticket[] = []

  outer: for (const redsCombo of redCombos) {
    for (const blue of uniqueBlues) {
      tickets.push({ reds: [...redsCombo], blue })
      if (tickets.length >= MAX_EXPANDED_TICKETS) break outer
    }
  }

  return {
    tickets,
    attempts: redCombos.length,
    warning: total > MAX_EXPANDED_TICKETS ? `共有 ${total} 注，页面仅展开前 ${MAX_EXPANDED_TICKETS} 注。` : undefined,
  }
}

export function ticketText(ticket: Ticket): string {
  return `${ticket.reds.map(formatNumber).join(' ')} + ${formatNumber(ticket.blue)}`
}

export function ticketsText(tickets: Ticket[]): string {
  return tickets.map((ticket, index) => `${String(index + 1).padStart(2, '0')}. ${ticketText(ticket)}`).join('\n')
}
